import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import GroupIcon from '@mui/icons-material/Group';
import {
    useGetMyOrganizations,
    useGetOrganizationUsers,
    useOrgAssignUser,
    useOrgRemoveUser,
    useSearchOrganizationUsers,
} from '../services/appService';

const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';
    try {
        const d = new Date(dateInput);
        if (isNaN(d.getTime())) return 'N/A';
        return d.toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
        return 'N/A';
    }
};

function OrgMembersPage() {
    const { appId } = useParams();
    const [orgs, setOrgs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Per-org expanded state: { [orgId]: { members, membersLoading, searchQ, searchResults, searchLoading } }
    const [orgState, setOrgState] = useState({});

    const getMyOrgsHook = useGetMyOrganizations();
    const getOrgUsersHook = useGetOrganizationUsers();
    const assignUserHook = useOrgAssignUser();
    const removeUserHook = useOrgRemoveUser();
    const searchUsersHook = useSearchOrganizationUsers();

    const fetchOrgs = useCallback(async () => {
        if (!appId) return;
        setLoading(true);
        setError('');
        try {
            const data = await getMyOrgsHook(appId);
            setOrgs(data || []);
            // Pre-init state per org
            const init = {};
            (data || []).forEach(org => {
                init[org.id] = { members: null, membersLoading: false, searchQ: '', searchResults: [], searchLoading: false };
            });
            setOrgState(init);
        } catch (err) {
            setError(err.message || 'Fehler beim Laden der Organisationen.');
        } finally {
            setLoading(false);
        }
    }, [appId, getMyOrgsHook]);

    useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

    const loadMembers = useCallback(async (orgId) => {
        setOrgState(prev => ({ ...prev, [orgId]: { ...prev[orgId], membersLoading: true } }));
        try {
            const members = await getOrgUsersHook(appId, orgId);
            setOrgState(prev => ({ ...prev, [orgId]: { ...prev[orgId], members: members || [], membersLoading: false } }));
        } catch (err) {
            setOrgState(prev => ({ ...prev, [orgId]: { ...prev[orgId], membersLoading: false } }));
            setError(err.message || 'Fehler beim Laden der Mitglieder.');
        }
    }, [appId, getOrgUsersHook]);

    // Load members for all orgs on mount
    useEffect(() => {
        orgs.forEach(org => {
            if (orgState[org.id]?.members === null) {
                loadMembers(org.id);
            }
        });
    }, [orgs]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = async (orgId, query) => {
        setOrgState(prev => ({ ...prev, [orgId]: { ...prev[orgId], searchQ: query, searchResults: [] } }));
        if (!query || query.length < 2) return;
        setOrgState(prev => ({ ...prev, [orgId]: { ...prev[orgId], searchLoading: true } }));
        try {
            const results = await searchUsersHook(appId, query);
            setOrgState(prev => ({ ...prev, [orgId]: { ...prev[orgId], searchResults: results || [], searchLoading: false } }));
        } catch {
            setOrgState(prev => ({ ...prev, [orgId]: { ...prev[orgId], searchLoading: false } }));
        }
    };

    const handleAssign = async (orgId, user) => {
        if (!user) return;
        try {
            await assignUserHook(appId, orgId, { user_id: user.id, role: 'member' });
            setOrgState(prev => ({ ...prev, [orgId]: { ...prev[orgId], searchQ: '', searchResults: [] } }));
            await loadMembers(orgId);
            // Update member count in org list
            setOrgs(prev => prev.map(o => o.id === orgId ? { ...o, member_count: (o.member_count || 0) + 1 } : o));
        } catch (err) {
            setError(err.message || 'Fehler beim Hinzufügen.');
        }
    };

    const handleRemove = async (orgId, userId) => {
        try {
            await removeUserHook(appId, orgId, userId);
            await loadMembers(orgId);
            setOrgs(prev => prev.map(o => o.id === orgId ? { ...o, member_count: Math.max(0, (o.member_count || 1) - 1) } : o));
        } catch (err) {
            setError(err.message || 'Fehler beim Entfernen.');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <GroupIcon color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h4">Meine Vereinsmitglieder</Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {orgs.length === 0 ? (
                <Alert severity="info">
                    Keine Organisationen gefunden. Bitte wende dich an einen Administrator.
                </Alert>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {orgs.map(org => {
                        const state = orgState[org.id] || {};
                        return (
                            <Paper key={org.id} variant="outlined" sx={{ p: 3 }}>
                                {/* Org Header */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {org.name}
                                            {org.is_verein && (
                                                <Chip label="Verein" size="small" color="primary" sx={{ ml: 1, verticalAlign: 'middle' }} />
                                            )}
                                        </Typography>
                                        {org.about_me && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {org.about_me}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Chip
                                        icon={<GroupIcon />}
                                        label={`${state.members?.length ?? org.member_count ?? 0} Mitglieder`}
                                        variant="outlined"
                                        size="small"
                                    />
                                </Box>

                                {org.invite_code && (
                                    <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1, display: 'inline-block' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Einladungscode:&nbsp;
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                            {org.invite_code}
                                        </Typography>
                                    </Box>
                                )}

                                <Divider sx={{ mb: 2 }} />

                                {/* Add member search */}
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Mitglied hinzufügen
                                </Typography>
                                <Autocomplete
                                    freeSolo
                                    options={state.searchResults || []}
                                    getOptionLabel={(option) =>
                                        typeof option === 'string'
                                            ? option
                                            : `${option.display_name || 'Unbekannt'} (${option.email || option.id})`
                                    }
                                    filterOptions={(x) => x}
                                    inputValue={state.searchQ || ''}
                                    onInputChange={(e, newVal) => handleSearch(org.id, newVal)}
                                    onChange={(e, value) => {
                                        if (value && typeof value !== 'string') {
                                            handleAssign(org.id, value);
                                        }
                                    }}
                                    loading={state.searchLoading}
                                    sx={{ mb: 3 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            placeholder="Name, E-Mail oder Telefon eingeben…"
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <PersonAddIcon sx={{ color: 'action.active', mr: 1, fontSize: 20 }} />
                                                        {params.InputProps.startAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => {
                                        // Build subtitle: show real email (skip pseudo @users.mylocalapp.de), then phone
                                        const pseudoSuffix = '@users.mylocalapp.de';
                                        const realEmail = option.email && !option.email.endsWith(pseudoSuffix) ? option.email : null;
                                        const authEmail = option.auth_email && !option.auth_email.endsWith(pseudoSuffix) ? option.auth_email : null;
                                        const displayEmail = realEmail || authEmail;
                                        const parts = [displayEmail, option.phone].filter(Boolean);
                                        const subtitle = parts.length > 0 ? parts.join(' · ') : option.id?.substring(0, 8);

                                        return (
                                            <li {...props} key={option.id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                                                    <Avatar
                                                        src={option.avatar_url}
                                                        sx={{ width: 36, height: 36 }}
                                                    >
                                                        {(option.display_name || '?')[0]}
                                                    </Avatar>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {option.display_name || 'Unbekannt'}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                        >
                                                            {subtitle}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </li>
                                        );
                                    }}
                                />

                                {/* Members list */}
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Aktuelle Mitglieder
                                </Typography>

                                {state.membersLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : !state.members || state.members.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Noch keine Mitglieder.
                                    </Typography>
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>Rolle</TableCell>
                                                    <TableCell>Beigetreten</TableCell>
                                                    <TableCell align="right">Aktion</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {state.members.map(member => (
                                                    <TableRow key={member.id} hover>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Avatar src={member.avatar_url} sx={{ width: 28, height: 28 }}>
                                                                    {(member.display_name || '?')[0]}
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        {member.display_name || 'Unbekannt'}
                                                                    </Typography>
                                                                    {member.is_verified && (
                                                                        <Chip label="Verifiziert" size="small" color="success" variant="outlined" sx={{ height: 16, fontSize: '0.65rem' }} />
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={member.role}
                                                                size="small"
                                                                color={member.role === 'admin' ? 'primary' : 'default'}
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {formatDate(member.joined_at)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleRemove(org.id, member.id)}
                                                                title="Mitglied entfernen"
                                                            >
                                                                <PersonRemoveIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Paper>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}

export default OrgMembersPage;
